/**
 * Pure helpers for detecting changes to an item's attribute rows.
 *
 * Attributes are edited in the UI as an array of `{ name, type, value }` rows.
 * The change-tracking in ItemDetails must treat add/edit/delete of any row as a
 * change so the Save button enables and the edit can persist (T1.4).
 */

/**
 * True when a stored attribute value uses the structured `{datatype, value}`
 * shape produced by `models.Requirement.add_attribute` (as opposed to a plain
 * scalar/object stored directly under the attribute name).
 * @param {*} value
 * @returns {boolean}
 */
export function isStructuredAttribute(value) {
  return (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.prototype.hasOwnProperty.call(value, 'datatype') &&
    Object.prototype.hasOwnProperty.call(value, 'value')
  );
}

/**
 * Render an inner attribute value as the editable string shown in the table.
 * Objects/arrays are JSON-encoded; everything else is stringified.
 * @param {*} value
 * @returns {string}
 */
function valueToEditString(value) {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Coerce an edited string back to a typed value according to the row's `type`.
 * Mirrors the type list offered by the editor; unknown/`object` types attempt a
 * best-effort JSON parse and fall back to the raw string.
 *
 * #654 (2): `type`/`datatype` for a STRUCTURED `{datatype,value}` attribute is a
 * caller-supplied free string (`models.Requirement.add_attribute(name, datatype,
 * value)` — see core/models.py), not limited to this editor's 4-option dropdown
 * list. Gating the JSON re-parse on the literal string `'object'` therefore
 * missed every structured attribute whose `datatype` was anything else (e.g.
 * `'json'`, `'risk_matrix'`, a custom label) — its object/array `value` still
 * round-tripped through the editable text field as a STRING, silently
 * corrupting the shape on save. `valueIsJson` is set independently, from what
 * the value actually WAS at load time (`attributesToRows`), so the re-parse
 * doesn't depend on guessing the datatype label at all.
 * @param {string} raw
 * @param {string} type
 * @param {boolean} [valueIsJson] - true when the original value was an object/array
 * @returns {*}
 */
function coerceFromEditString(raw, type, valueIsJson = false) {
  if (type === 'object' || valueIsJson) {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  switch (type) {
    case 'number': {
      const n = Number(raw);
      return Number.isNaN(n) ? raw : n;
    }
    case 'boolean':
      return raw === 'true' || raw === true;
    default:
      return raw;
  }
}

/**
 * Convert a stored attributes object (`{ name: value | {datatype,value} }`) into
 * editable rows. Structured attributes are unwrapped so the editor shows the
 * inner value with its declared `datatype`, and flagged so they round-trip back
 * to the same `{datatype, value}` shape on save (T1.4b).
 * @param {Object} attributesObj
 * @returns {Array<{name:string,type:string,value:string,structured:boolean}>}
 */
// #654 (2): does an object/array VALUE (independent of any datatype label).
const isJsonShaped = (v) => v != null && typeof v === 'object';

export function attributesToRows(attributesObj) {
  if (!attributesObj || typeof attributesObj !== 'object') return [];
  return Object.entries(attributesObj).map(([name, value]) => {
    if (isStructuredAttribute(value)) {
      return {
        name,
        type: value.datatype || typeof value.value,
        value: valueToEditString(value.value),
        structured: true,
        valueIsJson: isJsonShaped(value.value),
      };
    }
    return {
      name,
      type: typeof value,
      value: valueToEditString(value),
      structured: false,
      valueIsJson: isJsonShaped(value),
    };
  });
}

/**
 * Convert editor rows back into the stored attributes object, faithfully
 * round-tripping structured `{datatype, value}` attributes and coercing plain
 * values to their declared type (T1.4b). Later rows win on a name collision,
 * matching object-key semantics.
 * @param {Array<{name:string,type:string,value:*,structured?:boolean}>} rows
 * @returns {Object}
 */
export function rowsToAttributes(rows) {
  const out = {};
  (rows || []).forEach((row) => {
    if (!row || row.name == null || row.name === '') return;
    const coerced = coerceFromEditString(row.value, row.type, row.valueIsJson);
    out[row.name] = row.structured
      ? { datatype: row.type, value: coerced }
      : coerced;
  });
  return out;
}

/**
 * #654 (1): names duplicated across two or more rows (case-sensitive —
 * attribute keys are exact strings). `rowsToAttributes` builds its output
 * object with `out[row.name] = ...`, so a rename that collides with another
 * row's name doesn't error — it silently drops one row's data on Save ("later
 * rows win" is a plain-object-key fact, not a warning). This is what a caller
 * checks BEFORE trusting `rowsToAttributes`'s output for real, and what the
 * editor uses to flag the colliding rows so the user sees the problem before
 * it's too late.
 * @param {Array<{name:string}>} rows
 * @returns {Set<string>} names that appear on 2+ rows (empty = no collisions)
 */
export function duplicateAttributeNames(rows) {
  const seen = new Map();
  (rows || []).forEach((row) => {
    if (!row || row.name == null || row.name === '') return;
    seen.set(row.name, (seen.get(row.name) || 0) + 1);
  });
  const dupes = new Set();
  seen.forEach((count, name) => { if (count > 1) dupes.add(name); });
  return dupes;
}

/**
 * Order-independent canonical serialization of attribute rows.
 * @param {Array<{name:string,type:string,value:*}>} rows
 * @returns {string}
 */
export function serializeAttributeRows(rows) {
  return JSON.stringify(
    [...(rows || [])]
      .map((a) => ({
        name: a == null ? undefined : a.name,
        type: a == null ? undefined : a.type,
        value: a == null ? undefined : a.value,
      }))
      .sort((x, y) => String(x.name).localeCompare(String(y.name)))
  );
}

/**
 * True when the two sets of attribute rows differ (ignoring row order).
 * @param {Array} current
 * @param {Array} original
 * @returns {boolean}
 */
export function attributesChanged(current, original) {
  return serializeAttributeRows(current) !== serializeAttributeRows(original);
}
